import { API_ENDPOINTS } from '../constants/api';
import { API_CONFIG, ERROR_MESSAGES, ROUTES } from '../constants/app';
import { getAuthHeaders, clearAuthData } from './auth';
import { validateApiResponse } from './validation';

/**
 * API utility functions with error handling and retry logic
 */

// Default request timeout
const DEFAULT_TIMEOUT = API_CONFIG.TIMEOUT;

/**
 * Create a timeout promise
 * @param {number} ms - Timeout in milliseconds
 * @returns {Promise} Timeout promise
 */
const createTimeout = (ms) => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error('Request timeout')), ms);
  });
};

/**
 * Enhanced fetch with timeout and error handling
 * @param {string} url - Request URL
 * @param {Object} options - Fetch options
 * @param {number} timeout - Request timeout in ms
 * @returns {Promise} Response promise
 */
const fetchWithTimeout = async (url, options = {}, timeout = DEFAULT_TIMEOUT) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
        ...options.headers,
      },
    });
    
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw error;
  }
};

/**
 * Handle API response
 * @param {Response} response - Fetch response
 * @returns {Promise} Parsed response data
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    // Handle authentication errors
    if (response.status === 401) {
      clearAuthData();
      window.location.href = ROUTES.HOME;
      throw new Error(ERROR_MESSAGES.AUTH_REQUIRED);
    }
    
    // Try to get error message from response
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // If response is not JSON, use default error message
    }
    
    throw new Error(errorMessage);
  }
  
  const data = await response.json();
  return data;
};

/**
 * Retry function for failed requests
 * @param {Function} fn - Function to retry
 * @param {number} retries - Number of retries
 * @param {number} delay - Delay between retries in ms
 * @returns {Promise} Result of function
 */
const withRetry = async (fn, retries = API_CONFIG.RETRY_ATTEMPTS, delay = API_CONFIG.RETRY_DELAY) => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0 && !error.message.includes(ERROR_MESSAGES.AUTH_REQUIRED)) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return withRetry(fn, retries - 1, delay * 2); // Exponential backoff
    }
    throw error;
  }
};

/**
 * Generic API request function
 * @param {string} url - Request URL
 * @param {Object} options - Request options
 * @param {boolean} withRetryLogic - Whether to use retry logic
 * @returns {Promise} API response
 */
export const apiRequest = async (url, options = {}, withRetryLogic = true) => {
  const requestFn = async () => {
    const response = await fetchWithTimeout(url, options);
    return handleResponse(response);
  };
  
  if (withRetryLogic) {
    return withRetry(requestFn);
  }
  
  return requestFn();
};

/**
 * GET request
 * @param {string} url - Request URL
 * @param {Object} options - Request options
 * @returns {Promise} API response
 */
export const apiGet = (url, options = {}) => {
  return apiRequest(url, { ...options, method: 'GET' });
};

/**
 * POST request
 * @param {string} url - Request URL
 * @param {Object} data - Request body data
 * @param {Object} options - Request options
 * @returns {Promise} API response
 */
export const apiPost = (url, data, options = {}) => {
  return apiRequest(url, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * PUT request
 * @param {string} url - Request URL
 * @param {Object} data - Request body data
 * @param {Object} options - Request options
 * @returns {Promise} API response
 */
export const apiPut = (url, data, options = {}) => {
  return apiRequest(url, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * DELETE request
 * @param {string} url - Request URL
 * @param {Object} options - Request options
 * @returns {Promise} API response
 */
export const apiDelete = (url, options = {}) => {
  return apiRequest(url, { ...options, method: 'DELETE' });
};

/**
 * Authentication API calls
 */
export const authApi = {
  login: async (credentials) => {
    const response = await apiPost(API_ENDPOINTS.LOGIN, credentials, {}, false);
    if (!validateApiResponse(response, 'object') || !response.success) {
      throw new Error(response.message || ERROR_MESSAGES.INVALID_CREDENTIALS);
    }
    return response;
  },
  
  logout: async () => {
    try {
      await apiPost(API_ENDPOINTS.LOGOUT, {}, {}, false);
    } catch (error) {
      console.warn('Logout API call failed:', error.message);
    } finally {
      clearAuthData();
    }
  },
  
  verifyToken: async () => {
    const response = await apiGet(API_ENDPOINTS.VERIFY_TOKEN, {}, false);
    if (!validateApiResponse(response, 'object') || !response.success) {
      throw new Error(ERROR_MESSAGES.AUTH_REQUIRED);
    }
    return response;
  },
};

/**
 * Goodies API calls
 */
export const goodiesApi = {
  getAll: async () => {
    const response = await apiGet(API_ENDPOINTS.GOODIES);
    if (!validateApiResponse(response, 'object') || !response.success) {
      throw new Error(response.message || 'Failed to fetch goodies');
    }
    
    
    // Handle nested data structure from production API
    if (response.data && response.data.goodies && validateApiResponse(response.data.goodies, 'array')) {
      return {
        success: true,
        data: response.data.goodies,
        count: response.data.count || response.data.goodies.length,
        message: response.message
      };
    }
    
    // Handle direct array in data
    if (response.data && validateApiResponse(response.data, 'array')) {
      return response;
    }
    
    // Handle case where data might be empty or different format
    if (response.data === null || response.data === undefined) {
      console.warn('Goodies API returned null/undefined data, using empty array');
      return {
        success: true,
        data: [],
        count: 0,
        message: response.message || 'No goodies found'
      };
    }
    
    // If data exists but is not an array, try to handle it
    if (response.data && typeof response.data === 'object') {
      console.warn('Goodies API returned non-array data:', response.data);
      // Try to extract array from various possible structures
      const possibleArrays = [
        response.data.items,
        response.data.results,
        response.data.list,
        Object.values(response.data)
      ];
      
      for (const possibleArray of possibleArrays) {
        if (validateApiResponse(possibleArray, 'array')) {
          return {
            success: true,
            data: possibleArray,
            count: possibleArray.length,
            message: response.message
          };
        }
      }
    }
    
    throw new Error(`Invalid goodies data format. Expected array but got: ${typeof response.data}`);
  },
};

/**
 * Employee API calls
 */
export const employeeApi = {
  getAll: async () => {
    const response = await apiGet(API_ENDPOINTS.EMPLOYEES);
    if (!validateApiResponse(response, 'object') || !response.success) {
      throw new Error(response.message || 'Failed to fetch employees');
    }
    if (!validateApiResponse(response.data, 'array')) {
      throw new Error('Invalid employee data format');
    }
    return response;
  },
  
  getAllDetails: async () => {
    const response = await apiGet(API_ENDPOINTS.EMPLOYEES_DETAILS);
    if (!validateApiResponse(response, 'object') || !response.success) {
      throw new Error(response.message || 'Failed to fetch employee details');
    }
    
    // Handle nested data structure from production API
    if (response.data && response.data.employees && validateApiResponse(response.data.employees, 'array')) {
      return {
        success: true,
        data: response.data.employees,
        count: response.data.count || response.data.employees.length,
        message: response.message
      };
    }
    
    // Fallback for direct array in data
    if (!validateApiResponse(response.data, 'array')) {
      throw new Error('Invalid employee details data format');
    }
    return response;
  },
  
  getById: async (empId) => {
    const response = await apiGet(API_ENDPOINTS.EMPLOYEE_BY_ID(empId));
    if (!validateApiResponse(response, 'object') || !response.success) {
      throw new Error(response.message || ERROR_MESSAGES.EMPLOYEE_NOT_FOUND);
    }
    return response;
  },
  
  updateGoodies: async (empId, goodiesData) => {
    const response = await apiPut(API_ENDPOINTS.EMPLOYEE_GOODIES(empId), goodiesData);
    if (!validateApiResponse(response, 'object') || !response.success) {
      throw new Error(response.message || 'Failed to update goodies');
    }
    return response;
  },
  
  searchByName: async (searchTerm) => {
    const response = await apiGet(`${API_ENDPOINTS.EMPLOYEE_SEARCH}?search=${encodeURIComponent(searchTerm)}`);
    if (!validateApiResponse(response, 'object') || !response.success) {
      throw new Error(response.message || 'Failed to search employees');
    }
    if (!validateApiResponse(response.data, 'array')) {
      throw new Error('Invalid search results format');
    }
    return response;
  },
  
  print: async (empId) => {
    const response = await apiPost(API_ENDPOINTS.EMPLOYEE_PRINT(empId), {});
    if (!validateApiResponse(response, 'object') || !response.success) {
      throw new Error(response.message || 'Failed to send print request');
    }
    return response;
  },
  
  returnGoodies: async (empId, goodiesData) => {
    const response = await apiPost(API_ENDPOINTS.EMPLOYEE_GOODIES_RETURN(empId), goodiesData);
    if (!validateApiResponse(response, 'object') || !response.success) {
      throw new Error(response.message || 'Failed to return goodies');
    }
    return response;
  },
  
  completeGoodies: async (empId, goodiesData) => {
    const response = await apiPost(API_ENDPOINTS.EMPLOYEE_GOODIES_COMPLETE(empId), goodiesData);
    if (!validateApiResponse(response, 'object') || !response.success) {
      throw new Error(response.message || 'Failed to mark goodies as completed');
    }
    return response;
  },
};

/**
 * Error handling utility
 * @param {Error} error - Error object
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error) => {
  if (!error) return ERROR_MESSAGES.UNKNOWN_ERROR;
  
  if (error.message) {
    // Handle specific error types
    if (error.message.includes('timeout')) {
      return ERROR_MESSAGES.TIMEOUT_ERROR;
    }
    if (error.message.includes('Failed to fetch')) {
      return ERROR_MESSAGES.NETWORK_ERROR;
    }
    if (error.message.includes(ERROR_MESSAGES.AUTH_REQUIRED)) {
      return ERROR_MESSAGES.AUTH_REQUIRED;
    }
    return error.message;
  }
  
  return ERROR_MESSAGES.UNKNOWN_ERROR;
};
